import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

// Exchange rates relative to GBP (base currency)
const EXCHANGE_RATES = {
  GBP: 1,
  USD: 1.27,
  EUR: 1.17,
  CAD: 1.72,
  AUD: 1.94,
};

const CURRENCY_SYMBOLS = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: 'CA$',
  AUD: 'A$',
};

// Country to currency mapping
const COUNTRY_CURRENCY_MAP = {
  GB: 'GBP',
  US: 'USD',
  CA: 'CAD',
  AU: 'AUD',
  DE: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  IE: 'EUR',
  PT: 'EUR',
  // Default to GBP for unlisted countries
};

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState('GBP');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Detect user's location and set currency
    const detectCurrency = async () => {
      try {
        // Use free IP geolocation API
        const response = await fetch('https://ipapi.co/json/');
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code;
          const detectedCurrency = COUNTRY_CURRENCY_MAP[countryCode] || 'GBP';
          setCurrency(detectedCurrency);
        }
      } catch (error) {
        console.log('Could not detect location, defaulting to GBP');
        setCurrency('GBP');
      } finally {
        setLoading(false);
      }
    };

    detectCurrency();
  }, []);

  // Convert price from GBP to selected currency
  const convertPrice = (priceInGBP) => {
    const rate = EXCHANGE_RATES[currency] || 1;
    return (priceInGBP * rate).toFixed(2);
  };

  // Format price with currency symbol
  const formatPrice = (priceInGBP) => {
    const symbol = CURRENCY_SYMBOLS[currency] || '£';
    const convertedPrice = convertPrice(priceInGBP);
    return `${symbol}${convertedPrice}`;
  };

  // Get currency symbol
  const getSymbol = () => CURRENCY_SYMBOLS[currency] || '£';

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        convertPrice,
        formatPrice,
        getSymbol,
        loading,
        availableCurrencies: Object.keys(EXCHANGE_RATES),
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
